import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { StatusBadge, PriorityBadge, SlaBadge } from "../components/Badges";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { fmtDate } from "../lib/format";
import { Search } from "lucide-react";

export default function CustomerTickets() {
  const [q, setQ] = useState("");
  const { data: tickets, isLoading } = useQuery({ queryKey: ["my-tickets"], queryFn: () => api.get("/tickets").then((r) => r.data) });

  const filtered = (tickets || []).filter(
    (t) => t.subject.toLowerCase().includes(q.toLowerCase()) || t.ticket_number.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="font-heading font-black tracking-tighter text-4xl">My Tickets</h1>
        <p className="text-muted-foreground mt-1">All the issues you've reported</p>
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tickets…" className="pl-9" data-testid="ticket-search" />
      </div>

      <div className="rounded-lg border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-muted-foreground">
            <tr className="text-left">
              <th className="px-4 py-3 font-medium">Ticket</th>
              <th className="px-4 py-3 font-medium">Subject</th>
              <th className="px-4 py-3 font-medium">Priority</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Progress</th>
              <th className="px-4 py-3 font-medium">SLA</th>
              <th className="px-4 py-3 font-medium">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {isLoading ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">No tickets found.</td></tr>
            ) : (
              filtered.map((t) => (
                <tr key={t.id} className="hover:bg-secondary/40 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Link to={`/tickets/${t.id}`} data-testid={`ticket-link-${t.ticket_number}`} className="font-mono-plex text-xs text-primary hover:underline">{t.ticket_number}</Link>
                      {t.has_unread && (
                        <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                          {t.unread_count}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 max-w-[260px] truncate font-medium">
                    <div className="flex items-center gap-2">
                      <span>{t.subject}</span>
                      {t.has_unread && <span className="text-[10px] font-semibold uppercase tracking-wide text-red-500">Unread</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3"><PriorityBadge priority={t.priority} /></td>
                  <td className="px-4 py-3"><StatusBadge status={t.status} statusMeta={t.status_meta} /></td>
                  <td className="px-4 py-3 w-32">
                    <Progress value={t.progress} className="h-1.5" />
                    <span className="text-[10px] text-muted-foreground">{t.progress}%</span>
                  </td>
                  <td className="px-4 py-3"><SlaBadge status={t.sla.status} /></td>
                  <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(t.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
